<script>
	/**
	 * This top-level route displays an assessment of an assignment as well
	 * as data associated with the article of the assignment.
	 *
	 * SUB ROUTES
	 *
	 * This route has 5 sub-routes (tabs) that change based on user interaction.
	 *
	 * The parameter routeContext.params.tab controls which sub-route is visible.
	 * Consequently each of these sub-routes will create browser history entries.
	 * Refreshing the browser will also preserve the tab.
	 *
	 * These routes are:
	 * - ./assessment/class-results.vue - results for the whole class
	 * - ./assessment/student-results.vue - results for an individual student
	 * - ./assessment/questions.vue - question sets with student answers, grading
	 * - ./assessment/article.vue - the article content
	 * - ./article-tabs/question-sets.vue - question sets without student information
	 *
	 */
	var page = require('page');
	var _ = require('underscore');
	var model = require('model/model');
	var Utils = require('utilities');
	var cache = require('cache');
	var classService = require('services/classService');
	var assignmentService = require('services/assignmentService');
	var Grading = require('readworks-grading');
	var productService = require('services/productService');

	const csvUtilities = require('utilities/csvUtilities');
	const downloadCSV = csvUtilities.downloadCSV;

	const {
		fireEvent,
		events: { PRINT, EXPORT_CSV, WHITEBOARD_TOOL_CLICK, NEXT_STUDENT, PREVIOUS_STUDENT }
	} = require('utilities/analyticsUtilities');

	const classUtilities = require('utilities/classUtilities');
	const sortStudentsByName = classUtilities.sortStudentsByName;

	const ReadWorksAssignment = require('types/ReadWorksAssignment');
	const { takeAssignmentAction, ACTIONS, STATUS } = require('utilities/assignmentUtilities');

	const whiteboardViewMixin = require('mixins/whiteboard-view');
	const { assetUrl } = require('utilities/urlUtilities');
	const pubsub = require('events/pubsub');
	const { getBookStudyCategoryText } = require('utilities/articleUtilities');

	module.exports = {
		mixins: [whiteboardViewMixin],
		props: ['on-ready', 'route-context'],
		components: {
			results: require('./assessment/results-container.vue').default,
			assessmentarticle: require('./assessment/article.vue').default,
			stepreads: require('./assessment/stepreads.vue').default,
			questionsets: require('./article-tabs/question-sets.vue').default,
			ebook: require('./article-tabs/ebook.vue').default,
			bookofknowledge: require('./assessment/book-of-knowledge-results.vue').default,
			'video-tab': require('./article-tabs/video.vue').default,
			decodables: require('./article-tabs/decodables.vue').default,
			'graphic-organizer-results': require('./assessment/graphic-organizer-results.vue').default,
			'info-icon': require('components/svg/info-icon.vue').default,
			'mark-done-modal': require('components/mark-done-modal.vue').default,
			'dropdown-button': require('components/dropdown-button.vue').default,
			'assignment-close-modal': require('components/assignment-close-modal.vue').default,
			'assignment-delete-modal': require('components/assignment-delete-modal.vue').default,
			'change-deadline-modal': require('components/change-deadline-modal.vue').default,
			'show-results-modal': require('components/show-results-modal.vue').default,
			'load-mask': require('components/load-mask.vue').default,
			'tab-container': require('components/tab-container.vue').default,
			'tab-panel': require('components/tab-panel.vue').default,
			pagination: require('components/pagination.vue').default
		},
		mounted() {
			var self = this;

			var assignmentsBackUrl = self.routeContext.parsedPath.getQueryParam('abu');
			if (assignmentsBackUrl) {
				self.assignmentsBackUrl = assignmentsBackUrl;
				// if user clicks a link and abu is not preserved, add it back in so that we can
				// keep track of it if the user refreshes the page
				self.$watch('routeContext.querystring', function (qs) {
					if (!qs || qs.indexOf('abu=') === -1) {
						var uri = self.routeContext.pathname;
						uri += '?' + (qs ? qs + '&' : '') + 'abu=' + encodeURIComponent(assignmentsBackUrl);
						if (window.location.hash) {
							uri += window.location.hash;
						}
						page.redirect(uri);
					}
				});
			}

			self.$watch(
				'routeContext.params.classCode',
				cc => {
					this.loading = true;
					classService
						.getClassRoster({
							classCode: cc
						})
						.then(r => {
							self.roster = r;
							return self.loadAssessment().finally(() => (this.loading = false));
						});
				},
				{
					immediate: true
				}
			);

			self.$watch('routeContext.params.assignmentId', (aid, oldAid) => {
				if (oldAid) {
					model.set({
						assessmentTab: 'results'
					});
					model.remove('student');
					model.remove('resultsTab');
					model.remove('qset');
					model.remove('resultsSubTab');
					model.remove('questionsetsSection');
				}
				self.loadAssessment();
			});

			pubsub.subscribe('assessment-needs-reload', function () {
				self.loadAssessment();

				return true;
			});

			model.remove('workspacePreviewModalAssignment');
			cache.invalidateFullCache('teacherAnnotations');

			this.fromStudentProgress = this.studentProgressHash;
			this.studentProgressHash = null;

			// send ready notice to parent
			if (typeof this.onReady === 'function') {
				this.onReady();
			}

			// when user leaves the results tab, make sure student is blank, otherwise the back
			// link will continue to say "back to class results" when it should be "back to assignments"
			this.$watch(
				'assessmentTab',
				function (v) {
					const allowedTabs = ['results', 'assessmentarticle', 'stepreads', 'bookofknowledge', 'ebook', 'graphic-organizer-results'];
					if (!allowedTabs.some(id => v === id)) {
						this.studentForTab = '';
					}

					this.maybeOpenHighlightingModal();
				}.bind(this)
			);

			this.$watch('studentForTab', this.updateTotalAnnotations.bind(this), {
				immediate: true
			});

			this.$subscribe(ACTIONS.ASSIGNMENT_ACTION_BEGIN, () => (this.loading = true));
			this.$subscribe(ACTIONS.ASSIGNMENT_ACTION_END, () => {
				this.loadAssessment().finally(() => (this.loading = false));
			});
		},
		modelData: {
			resultsTab: 'resultsTab',
			student: 'studentForTab',
			studentProgressHash: 'studentProgressHash',
			assessmentTab: 'assessmentTab',
			localGCAccessToken: 'localGCAccessToken',
			whiteboardView: 'whiteboardView',
			whiteboardHighlightDismiss: 'whiteboardHighlightDismiss',
			splitTab: 'splitTab',
			gcAddon: 'gcAddon'
		},
		data: function () {
			return {
				loading: false,
				totalAnnotations: {},
				assignmentsBackUrl: '/assignments',
				allDataReady: false,
				article: {},
				assessment: {},

				orderedStudents: [],

				roster: {}, // TODO: we only need roster so we can get the class name and gcid, remove when server provides these in assessment

				// the currently selected question set (if any)
				questionSetId: null,

				// a sorted array of {id:'...', title:'...'} so we can render tabs
				questionSets: [],
				bookOfKnowledgeAssigned: false,
				studentsWithData: {},
				studentWorkspaces: {},
				fromStudentProgress: null,
				fullOrExpress: {},
				paragraphsNumbered: false,
				fontSizeClass: '',
				whiteboardDismissProduct: false,
				whiteboardViewData: {
					tabKey: 'assessmentTab',
					highlightingModalSelector: '#whiteboard-highlighting-warning',
					allowedHighlightingTabs: new Set(['assessmentarticle', 'stepreads'])
				},
				splitTabData: {
					tabKey: 'assessmentTab',
					defaultTab: 'assessmentarticle',
					eventName: WHITEBOARD_TOOL_CLICK
				},
				splitScreen: false,
				contentTabs: [],
				splitTabs: [],
				studentNames: {}
			};
		},
		computed: {
			showResults() {
				const whiteboardView = this.whiteboardView;
				const studentForTab = this.studentForTab;
				const studentsWithData = this.studentsWithData || {};
				const { qs, va = [] } = this.assessment.options || {};

				return !whiteboardView && (!studentForTab || (studentsWithData[studentForTab] && qs.length > 0) || va.length > 0);
			},
			assignmentDeadline() {
				let assessment = this.assessment;
				let studentForTab = this.studentForTab;
				let studentWorkspaces = this.studentWorkspaces || {};

				if (!this.assessment) {
					return null;
				}
				if (studentForTab && studentWorkspaces[studentForTab] && studentWorkspaces[studentForTab].edd) {
					return studentWorkspaces[studentForTab].edd;
				}

				return assessment.deadline;
			},
			hasAudio() {
				const options = this.assessment.options;
				return options && options.au;
			},
			hasVideo() {
				const options = this.assessment.options;
				return options && options.video;
			},
			graphicOrganizerAssigned() {
				let assessment = this.assessment;
				let go = null;

				if (assessment && assessment.options) {
					if (assessment.options.wd) {
						go = 'wd';
					} else if (assessment.options.mm) {
						go = 'mm';
					}
				}
				return go;
			},
			tabs() {
				const article = this.article;
				const { productType, hasVideo } = article || {};
				const assessment = this.assessment;
				const totalAnnotations = this.totalAnnotations;
				const questionSets = this.questionSets;

				const isAAD = article && article.productType === 'AAD';

				const showBOK = isAAD && this.bookOfKnowledgeAssigned;

				const graphicOrganizerAssigned = this.graphicOrganizerAssigned;
				const showGraphicOrganizer = isAAD && graphicOrganizerAssigned;
				let graphicOrganizerLabel = 'Meaning Mapper';
				let graphicOrganizerHint;
				if (graphicOrganizerAssigned === 'wd') {
					graphicOrganizerLabel = 'Word Detective';
					graphicOrganizerHint =
						'Word Detective is a 15-minute, teacher-led investigation of word parts that is designed to be an in-person or virtual class discussion on the last day of the Article-A-Day routine.';
				}

				const labelMap = new Map([
					['A', 'Passage'],
					['P', 'Passages'],
					['PVideo', 'Passage & Video'],
					['AAD', 'Articles']
				]);

				let key = productType;
				if (hasVideo && productType === 'P') {
					key = 'PVideo';
				}

				const articleLabel = labelMap.get(key) || '';

				const showStepReads = assessment && assessment.options && assessment.options.sr && assessment.options.sr.length;

				const showQuestionSets = questionSets && questionSets.length && !this.studentForTab;

				const tabs = [
					{
						id: 'results',
						text: 'Results',
						renderCondition: this.showResults
					},
					{
						id: 'bookofknowledge',
						text: 'Book of Knowledge',
						split: 'activity',
						renderCondition: showBOK
					},
					{
						id: 'graphic-organizer-results',
						text: graphicOrganizerLabel,
						hint: graphicOrganizerHint,
						renderCondition: showGraphicOrganizer
					},
					{
						id: 'assessmentarticle',
						text: articleLabel,
						split: 'content',
						renderCondition: true,
						showAnnotations: totalAnnotations && totalAnnotations.assessmentarticle > 0
					},
					{
						id: 'stepreads',
						text: 'StepReads™',
						split: 'content',
						renderCondition: showStepReads,
						showAnnotations: totalAnnotations && totalAnnotations.stepreads > 0
					},
					{
						id: 'video-tab',
						text: 'Video',
						split: 'content',
						renderCondition: this.hasVideo
					},
					{
						id: 'questionsets',
						text: 'Question Set',
						split: 'activity',
						renderCondition: showQuestionSets
					},
					{
						id: 'ebook',
						text: 'eBook',
						renderCondition: assessment && assessment.options && assessment.options.ebook
					},
					{
						id: 'decodables',
						text: 'Decodables',
						renderCondition: assessment && assessment.options && assessment.options.decodables
					}
				];

				return tabs.filter(({ renderCondition }) => renderCondition);
			},
			printLogoSrc() {
				return assetUrl('/assets/dist/images/rw-bw.jpeg');
			},
			articleSubtitle() {
				const textSet = this.assessment && this.assessment.textSet;

				if (textSet) {
					if (textSet.subType === 7000) {
						const category = getBookStudyCategoryText(textSet.category);

						return `${category} Book Study`;
					}
				}

				const article = this.article;

				if (article) {
					const { productType, hasVideo, subType } = article;

					if (productType === 'P') {
						if (subType === 8001) {
							return 'Spanish-English Paired Text';
						}

						if (hasVideo) {
							return 'Paired Video';
						}

						return 'Paired Text';
					}

					if (productType === 'AAD') {
						const suffix = this.studentForTab ? 'Results' : '';
						return `Article-A-Day<sup><small>TM</small></sup> ${suffix}`;
					}
				}

				return '';
			},
			currentStudentIndex() {
				const studentForTab = this.studentForTab;
				const orderedStudents = this.orderedStudents;

				if (studentForTab && Array.isArray(orderedStudents)) {
					return orderedStudents.findIndex(student => student.uid === studentForTab);
				}

				return 0;
			}
		},
		watch: {
			tabs(tabs) {
				this.contentTabs = tabs.filter(t => t.split === 'content');
				this.splitTabs = tabs.filter(t => t.split === 'activity');
			}
		},
		beforeDestroy() {
			model.set('activeProduct', null);
		},
		methods: {
			updateTotalAnnotations: function () {
				this.totalAnnotations = {};

				var s = this.studentForTab;

				if (!s || !this.studentWorkspaces[s]?.hasWorkspace) {
					return;
				}

				assignmentService
					.teacherGetAnnotations({
						assignmentId: this.routeContext.params.assignmentId,
						student: s
					})
					.then(
						function (annotations) {
							// student changed before response came back, so don't use the data
							if (this.studentForTab !== s) {
								return;
							}

							if (!this.article) {
								return;
							}

							var total = {
								assessmentarticle: 0,
								stepreads: 0
							};
							var article = this.article;

							if (annotations) {
								Object.keys(annotations).forEach(
									function (uuid) {
										total[uuid] = Object.keys(annotations[uuid]).length;

										if (uuid === article.uuid) {
											total.assessmentarticle += Object.keys(annotations[uuid]).length;
										} else if (['P', 'AAD'].includes(article.productType) && article.articles[uuid]) {
											total.assessmentarticle += Object.keys(annotations[uuid]).length;
										} else if (article.sr) {
											(article.articleList || []).forEach(function (sr) {
												if (sr.uuid === uuid) {
													total.stepreads += Object.keys(annotations[uuid]).length;
												}
											});
										}
									}.bind(this)
								);
							}

							this.totalAnnotations = total;
						}.bind(this)
					);
			},
			navigateToResults: function () {
				model.set({
					resultsTab: 'classresults',
					assessmentTab: 'results'
				});
			},
			backToClassResults: function () {
				model.set({
					student: '',
					resultsTab: 'classresults',
					assessmentTab: 'results'
				});
			},
			backToStudentProgress: function () {
				let studentProgressHash = this.fromStudentProgress;
				page('/assignments');
				model.set(studentProgressHash);
				this.fromStudentProgress = null;
			},
			openReassignModal: function () {
				Utils.openModal('#modal-reassign', {
					// the student property in this component is missing uid, so add it from studentAssessment
					student: $.extend(
						true,
						{
							uid: this.studentForTab
						},
						this.assessment.students[this.studentForTab]
					),
					assessment: this.assessment,
					assignmentId: this.routeContext.params.assignmentId,
					roster: this.roster
				});
			},
			loadAssessment: function () {
				let self = this;
				let params = self.routeContext.params;

				self.studentsWithData = {};
				self.studentWorkspaces = {};

				this.loading = true;

				return assignmentService
					.teacherGetAssignmentResultsSummary({
						assignmentId: params.assignmentId,
						classCode: params.classCode
					})
					.then(function (a) {
						if (a) {
							if (a.assignmentStatus === STATUS.DELETED) {
								let url = self.assignmentsBackUrl || '/assignments#!classCode:' + params.classCode;
								page(url);
								return;
							}
							productService.get(a.productId).then(function (p) {
								a.product = p;

								// turn student answers for each question set into an array
								_.each(a.students, function (student) {
									_.each(p.questionSets, function (qset, qsetId) {
										var studentAnswers = [];

										_.each(qset.questions, function (q) {
											var ans;
											if (student.answers[qsetId] && student.answers[qsetId][q.qid]) {
												ans = student.answers[qsetId][q.qid];

												if (!('teacherNote' in ans)) {
													ans.teacherNote = null;
												}
											} else {
												ans = {
													questionType: q.type,
													answered: false
												};
												if (q.type === Grading.TYPE_WHO_WHAT) {
													ans.subQuestions = {};
													_.each(q.subQuestions, function (sq) {
														// do not add first, pre-answered question
														if (q.subQuestions[0].qid !== sq.qid) {
															ans.subQuestions[sq.qid] = {
																questionType: Grading.TYPE_WHO_WHAT,
																answered: false,
																score: undefined,
																qid: sq.qid
															};
														}
													});
												}
											}
											ans.qid = q.qid;
											studentAnswers.push(ans);
										});

										student.answers[qsetId] = studentAnswers;
									});
								});

								self.article = $.extend(true, {}, a.product);
								model.set('activeProduct', self.article);
								self.assessment = new ReadWorksAssignment(a);
								let organizers = {};
								let hasGraphicOrganizers = a.graphicOrganizers && a.graphicOrganizers.length && (a.options.wd || a.options.mm);

								if (hasGraphicOrganizers) {
									organizers = a.graphicOrganizers.reduce((acc, go) => {
										return _.assign(acc, {
											[go.uid]: go
										});
									}, {});
								}
								// if the student has nothing answered and has not submitted, then we don't
								// want to show the Results tab for when that student is selected, so we
								// make a simple lookup table here by UID that we can check to show/hide
								// the results tab
								Object.keys(self.assessment.students).forEach(function (uid) {
									var student = self.assessment.students[uid];

									if (student) {
										if (hasGraphicOrganizers) {
											self.assessment.students[uid].graphicOrganizer = organizers[uid];
										}
										self.studentWorkspaces[uid] = self.assessment.students[uid];
									}

									Object.keys(self.assessment.students[uid].answers).forEach(function (qsid) {
										Grading.eachQuestionAndSubQuestion(self.assessment.students[uid].answers[qsid], function (q) {
											if (student.submitted || q.answered || q.teacherNote || (student.generalNotes && student.generalNotes[qsid])) {
												self.studentsWithData[uid] = 1;
											}
										});
									});
								});

								self.bookOfKnowledgeAssigned = a.options.bok;

								var qs = [],
									opts = a.options;

								_.each(self.article.questionSetsOrdered, function (qset) {
									if (_.contains(opts.qs, parseInt(qset.qsid))) {
										qs.push({
											id: qset.qsid,
											title: qset.title
										});
									}
								});

								self.questionSets = qs;
								const gcAddon = self.gcAddon || {};

								var students = [];
								_.each(self.assessment.students, function (s, uid) {
									students.push(
										$.extend(
											true,
											{
												uid: uid
											},
											s
										)
									);

									if (gcAddon.submissionId && s.gcaSubmissionId === gcAddon.submissionId) {
										model.set({
											student: uid,
											resultsTab: 'studentresults'
										});
									}
								});
								students.sort(sortStudentsByName);

								var studentUsernameByUid = {};
								self.roster.students.forEach(function (s) {
									studentUsernameByUid[s.uid] = s.username;
								});

								students.forEach(function (s) {
									s.username = studentUsernameByUid[s.uid];
								});

								self.studentNames = Utils.unambiguousStudentLabels(students);
								self.orderedStudents = students;

								// don't render the tab component until the base line data is loaded
								self.allDataReady = true;

								self.updateTotalAnnotations();
								self.$emit('data-ready');
							});
						} else {
							pubsub.publish('message-error', 'Invalid assessment');
							page('/assignments');
						}
					})
					.finally(() => (this.loading = false));
			},
			printThis() {
				let resultsTab = this.resultsTab;
				let section = 'INDIVIDUAL_STUDENT_RESULTS';
				let sid = this.studentForTab;
				let aid = this.routeContext.params.assignmentId;
				let cc = this.routeContext.params.classCode;
				let subsection = model.get('qset');

				if (resultsTab === 'classresults') {
					section = 'CLASS_RESULTS';
				}

				fireEvent(PRINT, {
					section,
					subsection,
					sid,
					aid,
					cc
				});
				window.print();
			},
			exportData() {
				let assessment = this.assessment;
				let className = this.roster && this.roster.name;
				let article = this.article;
				let assignmentDeadline = this.assignmentDeadline;
				let rawData = model.get('assessmentExportData');
				let qsetId = model.get('qset');
				let qset = qsetId ? assessment.questionSets[qsetId] : null;
				let qsetName = qset ? qset.name : null;
				let aid = this.routeContext.params.assignmentId;
				let cc = this.routeContext.params.classCode;

				if (rawData && rawData.length) {
					let lastPosition = rawData[0].length - 1;
					let metaData = [];

					let classNameMeta = [];
					classNameMeta[0] = 'Class';
					classNameMeta[lastPosition] = className;
					metaData.push(classNameMeta);

					let assignmentNameMeta = [];
					assignmentNameMeta[0] = 'Assignment';
					assignmentNameMeta[lastPosition] = article.title;
					metaData.push(assignmentNameMeta);

					if (article.productType !== 'AAD') {
						let qsetNameMeta = [];
						qsetNameMeta[0] = 'Question Set';
						qsetNameMeta[lastPosition] = qsetName;
						metaData.push(qsetNameMeta);
					}

					let startDateMeta = [];
					startDateMeta[0] = 'Start Date';
					startDateMeta[lastPosition] = Utils.getSimpleDateString(assessment.startDate || assessment.creationTimestamp);
					metaData.push(startDateMeta);

					if (assignmentDeadline) {
						let endDateMeta = [];
						endDateMeta[0] = article.productType === 'AAD' ? 'End Date' : 'Due Date';
						endDateMeta[lastPosition] = Utils.getSimpleDateString(assignmentDeadline);
						metaData.push(endDateMeta);
					}

					metaData.push([]);

					const data = metaData.concat(rawData);

					downloadCSV(data, 'ReadWorks_' + className + '.csv');
					fireEvent(EXPORT_CSV, {
						cc,
						aid,
						qsetId
					});
				}
			},
			previewWorkspace() {
				let assessment = this.assessment;
				let classCode = this.routeContext.params.classCode;
				let assignmentId = this.routeContext.params.assignmentId;
				let url = '';

				if (assessment.product.productType !== 'AAD') {
					url = '/student-workspace/';
				} else {
					url = '/article-dashboard/';
				}
				page(url + assignmentId + '?classCode=' + classCode);
			},
			onOptionClick(option) {
				takeAssignmentAction(option.value, this.assessment, this.roster);
			},
			setFullOrExpress(fullOrExpress) {
				this.fullOrExpress = Object.assign({}, fullOrExpress);
			},
			changeStudent(studentIndex, event) {
				const suid = this.orderedStudents[studentIndex].uid;
				const { assignmentId: aid, classCode: cc } = this.routeContext.params;

				this.studentForTab = suid;

				fireEvent(event, {
					loc: 'individual-student-results',
					suid,
					cc,
					aid
				});
			},
			previousStudent(studentIndex) {
				this.changeStudent(studentIndex, PREVIOUS_STUDENT);
			},
			nextStudent(studentIndex) {
				this.changeStudent(studentIndex, NEXT_STUDENT);
			}
		}
	};
</script>
<style lang="scss">
	@import 'mixins/flexbox';
	@import 'mixins/media';
	@import 'config/colors';

	.assessment {
		.main-header {
			border-bottom: none;

			> .wrapper {
				.main-header-back {
					float: none;
				}

				.assessment-header {
					display: flex;
					flex-wrap: wrap;

					.assessment-title {
						flex: 1;

						.title-container {
							display: flex;
							flex-wrap: wrap;
							h2 {
								margin-right: 1rem;
							}
						}
					}

					.main-header-title {
						float: none;
						clear: none;
					}

					.main-header-meta {
						float: none;
					}
				}

				&.student-header-wrapper {
					.main-header-back-container {
						display: flex;
						align-items: center;
						justify-content: space-between;

						@include media(sm, max) {
							flex-direction: column;
							align-items: flex-start;

							.pagination {
								margin-left: auto;
							}
						}
					}

					.assessment-header {
						flex-direction: column;
					}

					.assessment-title {
						display: flex;
						align-items: baseline;
						margin: 0 0 0.5rem;
						flex-wrap: wrap;

						@include media(sm, max) {
							margin-top: 0.5rem;
						}
					}

					.class-name,
					.article-header-meta {
						line-height: 1.35;
					}

					.class-name {
						color: $c-slate-dark;
						display: block;

						&:hover {
							color: $c-blue-dark;
						}
					}

					.main-header-title {
						flex: 1 1 100%;
						margin-right: 0.5rem;
						white-space: nowrap;

						@include media(med) {
							flex: 0 0;
						}
					}

					.main-header-meta {
						text-align: left;
					}

					.article-title {
						margin: 0;
						font-weight: 500;
						display: inline;

						@include media(med, max) {
							font-size: 1.35rem;
						}
					}

					.article-header-meta {
						margin-bottom: 0.25rem;
					}

					.student-assessment-reassign-button {
						color: $c-blue-dark;
						font-weight: 500;
						padding: 0.25rem 1rem;
						border-radius: 24px;

						&:hover {
							color: $c-slate-dark;
							background-color: $c-grey-light;
						}
					}
				}
			}
		}

		.tab-wrapper {
			position: sticky;
			top: 0;
			z-index: 3;
			background-color: $c-blue-lighter;
			border-bottom: 1px solid $c-grey;

			.wrapper {
				display: flex;
			}
		}

		.assessment-tools {
			display: flex;
			flex: 1;
			justify-content: flex-end;
			align-items: center;

			button {
				margin: 0 0.5rem;
			}

			.assignment-options {
				margin-left: 0.75rem;

				.dropdown-button {
					position: relative;

					.title-text {
						font-size: 1.25rem;
					}
				}
			}
		}

		.whiteboard-view & {
			.main-header {
				.main-header-title {
					font-size: 1.5rem;
					margin-bottom: 0;
				}
			}
		}
	}

	@media print {
		.assessment.main {
			font-size: 0.75em;

			header.main-header {
				border-bottom: none;
				padding: 0rem;

				> .wrapper {
					margin-left: 0rem !important;
					margin-right: 0rem !important;
					width: 100%;
					max-width: 100%;

					.print-header.print-only {
						width: 100%;
						@include flexbox();
						@include align-items(center);

						.print-logo {
							flex: 0 0 110px;
						}

						h1.main-header-title {
							display: block;
							font-size: 14px !important;
							text-align: right;

							& > .class-name.label {
								font-size: 12px !important;
								margin-right: 0rem;
							}
						}
					}
				}
			}
		}
	}
</style>
<template>
	<main
		id="content"
		class="main assessment"
		:class="[
			fontSizeClass,
			{
				'split-screen': splitScreen
			}
		]"
		role="main">
		<load-mask v-if="loading"></load-mask>
		<header class="main-header -has-nav">
			<div
				v-if="studentForTab && assessment.students && assessment.students[studentForTab]"
				class="wrapper student-header-wrapper">
				<div class="main-header-back-container">
					<a
						v-if="fromStudentProgress"
						href="/assignments"
						@click.prevent="backToStudentProgress"
						class="main-header-back">
						&larr; Back to Student Progress
					</a>
					<a
						v-else-if="!gcAddon"
						v-on:click.prevent="backToClassResults"
						class="main-header-back">
						&larr; Back to Class Results
					</a>
					<pagination
						v-if="!gcAddon && orderedStudents.length > 1"
						class="print-hidden"
						label="Student"
						:page="currentStudentIndex"
						:total="orderedStudents.length"
						:page-size="1"
						:show-arrow-text="false"
						allow-wrap
						variant="minimal"
						:on-previous-page="previousStudent"
						:on-next-page="nextStudent"></pagination>
				</div>
				<div class="print-header print-only">
					<div class="print-logo">
						<img :src="printLogoSrc" />
					</div>
					<h1 class="main-header-title">
						<div class="student-name">
							{{ assessment.students[studentForTab].firstName }}
							{{ assessment.students[studentForTab].lastName }}
						</div>
						<div v-if="roster && roster.name">
							<span class="class-name label">{{ roster.name }}</span>
						</div>
					</h1>
				</div>
				<div class="assessment-header">
					<div class="assessment-title">
						<h1
							class="main-header-title print-hidden"
							aria-live="polite"
							v-if="!splitScreen">
							{{ studentNames[studentForTab] }}
						</h1>
						<a
							class="class-name"
							:href="`/class-admin#!classAdmin:roster/classCode:${routeContext.params.classCode}`">
							{{ roster.name }}
						</a>
					</div>
					<div class="main-header-meta print-hidden">
						<div class="article-title-container">
							<h2
								class="article-title"
								v-text="article && article.title"></h2>
							<span
								v-if="articleSubtitle"
								class="assessment-subtitle font-small margin-left-xs"
								v-html="articleSubtitle"></span>
						</div>
						<div class="article-header-meta">
							<span class="header-meta-item">
								<span>Start Date: </span>
								<time>{{ assessment && (assessment.startDate || assessment.creationTimestamp) | date }}</time>
							</span>
							<template v-if="assignmentDeadline">
								<span> - </span>
								<span class="header-meta-item">
									<span v-text="article && article.productType === 'AAD' ? 'End Date: ' : 'Due Date:'"></span>
									<time>{{ assignmentDeadline | date }}</time>
								</span>
							</template>
							<span class="header-meta-item">
								<button
									v-if="
										(assessment.students[studentForTab].submitted ||
											(assessment.closed && typeof assessment.students[studentForTab].submitted === 'boolean')) &&
										article.productType !== 'AAD'
									"
									v-on:click.prevent="openReassignModal"
									class="button -link student-assessment-reassign-button">
									<i class="fa-regular fa-calendar"></i>
									Reassign
								</button>
							</span>
						</div>
					</div>
				</div>
			</div>
			<div
				v-else
				class="wrapper class-header-wrapper">
				<div class="main-header-back-container">
					<a
						v-if="!gcAddon"
						:href="assignmentsBackUrl"
						class="main-header-back">
						&larr; Assignments
					</a>
				</div>
				<div class="print-header print-only">
					<div class="print-logo">
						<img :src="printLogoSrc" />
					</div>
					<h1 class="main-header-title">
						<div v-if="roster && roster.name">
							<span class="class-name label">{{ roster.name }}</span>
						</div>
					</h1>
				</div>
				<div class="assessment-header">
					<div class="assessment-title">
						<div class="title-container">
							<h2
								class="main-header-title print-hidden"
								v-if="!splitScreen">
								{{ article && article.title }}
							</h2>
							<button
								v-if="!whiteboardView && assessment && assessment.product"
								@click.prevent="previewWorkspace"
								class="button student-view">
								Student View
							</button>
						</div>
						<div
							v-if="articleSubtitle"
							class="assessment-subtitle font-small"
							v-html="articleSubtitle"></div>
					</div>
					<div class="main-header-meta print-hidden">
						<h3>
							<a :href="`/class-admin#!classAdmin:roster/classCode:${routeContext.params.classCode}`">{{ roster.name }}</a>
						</h3>
						<div>
							Start Date:
							<time>{{ assessment && (assessment.startDate || assessment.creationTimestamp) | date }}</time>
						</div>
						<div v-if="assignmentDeadline">
							{{ article && article.productType === 'AAD' ? 'End Date' : 'Due Date' }}:
							<time>{{ assignmentDeadline | date }}</time>
						</div>
					</div>
				</div>
			</div>
		</header>
		<div class="tab-wrapper">
			<div class="wrapper">
				<tab-container
					v-show="!splitScreen"
					:tabs="tabs"
					model-key="assessmentTab">
					<div
						class="tab-with-hint"
						v-for="tab in tabs"
						:key="tab.id">
						<button :data-tab="tab.id">
							{{ tab.text }}
							<template v-if="tab.showAnnotations">(Highlighted)</template>
						</button>
						<button
							class="button -link tab-hint"
							v-if="tab.hint"
							v-tooltip="tab.hint">
							<sup>
								<info-icon></info-icon>
							</sup>
						</button>
					</div>
				</tab-container>
				<div
					class="assessment-tools"
					v-if="!loading">
					<template v-if="whiteboardView">
						<button
							class="font-size-button button -small -outline tooltip-bottom"
							v-tooltip="'Change Font Size'"
							@click="onFontSizeClick">
							<i
								class="icon-font-size"
								aria-hidden="true"></i>
						</button>
						<button
							v-if="assessmentTab === 'assessmentarticle'"
							class="paragraph-numbers-button button -small -outline tooltip-bottom"
							:class="{
								'-active': paragraphsNumbered
							}"
							v-tooltip="'Paragraph Numbers'"
							:aria-pressed="`${paragraphsNumbered}`"
							@click="onParagraphNumbersClick">
							<i
								class="fa-solid fa-paragraph"
								aria-hidden="true"></i>
						</button>
						<button
							v-if="article && article.productType !== 'AAD' && splitTabs.length > 0"
							class="button -outline toolbar-button -small split-screen-button tooltip-bottom"
							:class="{
								'-active': splitScreen
							}"
							v-tooltip="'Use split screen'"
							:aria-pressed="`${splitScreen}`"
							@click="onSplitScreenClick">
							<i
								class="fa-solid fa-table-columns"
								aria-hidden="true"></i>
						</button>
					</template>
					<button
						v-if="assessmentTab !== 'results'"
						class="whiteboard-button button -small tooltip-bottom"
						:class="{
							'-outline': whiteboardView
						}"
						v-tooltip="whiteboardView ? 'Exit Whiteboard/Projector view' : 'Enter Whiteboard/Projector view'"
						@click="onWhiteboardViewClick">
						<i
							class="fa-solid"
							:class="{
								'fa-xmark': whiteboardView,
								'fa-desktop': !whiteboardView
							}"
							aria-hidden="true"></i>
					</button>
					<div
						v-if="!studentForTab && !whiteboardView"
						class="assignment-options">
						<dropdown-button
							class="print-hidden"
							slot="actions"
							@click-item="onOptionClick"
							:items="assessment && assessment.actions"
							:data-item="assessment"
							:icon-cls="'cog fa-lg fa-solid'"></dropdown-button>
					</div>
				</div>
			</div>
		</div>
		<div
			class="assessment-body"
			:class="{
				'split-screen-content': splitScreen
			}"
			v-if="allDataReady"
			ref="assessmentTabPanel"
			:data-panel="assessmentTab">
			<component
				v-if="!splitScreen"
				:total-annotations="totalAnnotations"
				:route-context="routeContext"
				:article="article"
				:ordered-students="orderedStudents"
				:question-sets="questionSets"
				:assessment="assessment"
				:assignment-id="routeContext.params.assignmentId"
				:bok-tab-name="'bokResultsByStudent'"
				:assignment-deadline="assignmentDeadline"
				:is="assessmentTab"
				:show-tts="hasAudio"
				:print-view="printThis"
				:export-data="exportData"
				:paragraphs-numbered="paragraphsNumbered"
				:whiteboard-view="whiteboardView"
				:full-or-express="fullOrExpress"
				:set-full-or-express="setFullOrExpress"
				:roster="roster"></component>
			<template v-if="splitScreen">
				<tab-panel
					:tabs="contentTabs"
					:tab-key="'assessmentTab'"
					:enable-snap-to-top="false">
					<template slot="tab-panel-body">
						<h2
							v-if="article && article.productType === 'A'"
							class="margin-top-bottom-small">
							{{ article && article.title }}
						</h2>
						<component
							:total-annotations="totalAnnotations"
							:route-context="routeContext"
							:article="article"
							:ordered-students="orderedStudents"
							:question-sets="questionSets"
							:assessment="assessment"
							:assignment-id="routeContext.params.assignmentId"
							:bok-tab-name="'bokResultsByStudent'"
							:assignment-deadline="assignmentDeadline"
							:is="assessmentTab"
							:show-tts="hasAudio"
							:print-view="printThis"
							:export-data="exportData"
							:paragraphs-numbered="paragraphsNumbered"
							:whiteboard-view="whiteboardView"
							:full-or-express="fullOrExpress"
							:set-full-or-express="setFullOrExpress"
							:roster="roster"></component>
					</template>
				</tab-panel>
				<tab-panel
					:tabs="splitTabs"
					:tab-key="'splitTab'"
					:enable-snap-to-top="false">
					<component
						slot="tab-panel-body"
						:total-annotations="totalAnnotations"
						:route-context="routeContext"
						:article="article"
						:ordered-students="orderedStudents"
						:question-sets="questionSets"
						:assessment="assessment"
						:assignment-id="routeContext.params.assignmentId"
						:bok-tab-name="'bokResultsByStudent'"
						:assignment-deadline="assignmentDeadline"
						:is="splitTab"
						:show-tts="hasAudio"
						:print-view="printThis"
						:export-data="exportData"
						:paragraphs-numbered="paragraphsNumbered"
						:whiteboard-view="whiteboardView"
						:full-or-express="fullOrExpress"
						:set-full-or-express="setFullOrExpress"
						:roster="roster"></component>
				</tab-panel>
			</template>
		</div>
		<reassign-modal></reassign-modal>
		<assignment-modal
			show-article-title
			:product-id="assessment.productId"></assignment-modal>
		<show-results-modal></show-results-modal>
		<change-deadline-modal></change-deadline-modal>
		<assignment-delete-modal></assignment-delete-modal>
		<assignment-close-modal></assignment-close-modal>
		<!-- <mark-done-modal :loading="doneGradingLoading"
		:gcid="roster.gcid"
		@done-grading="doneGrading"></mark-done-modal> -->
		<modal
			id="whiteboard-highlighting-warning"
			:closeable="false"
			:no-auto-close="true">
			<div slot="content">
				<p v-if="article.productType !== 'AAD'">Any highlights and annotations you create will not be saved.</p>
				<template v-if="article.productType === 'AAD'">
					<p>Any highlights you create will not be saved.</p>
					<p>
						Only one color is available for highlighting in Article-A-Day sets to help students focus on marking the background knowledge and vocabulary
						they have learned, the goal of the Article-A-Day routine.
					</p>
				</template>
				<div class="text-centered margin-top-large">
					<button
						class="button"
						@click="onCloseWhiteboardModalClick">
						Acknowledge
					</button>
					<div
						class="font-small link text-muted margin-top-medium"
						@click="onWhiteboardHighlightDismiss">
						Don't show this message again.
					</div>
				</div>
			</div>
		</modal>
	</main>
</template>
